import { useState } from 'react'
import { CalendarX, Siren } from 'lucide-react'
import { Button, EndpointHint, Skeleton, type SessionRole } from '../../core/index.js'
import { useFacilityConfig } from '../../facilities/index.js'
import { useBridgeCallouts, useCancelBridgeCallout } from '../hooks.js'
import type { BridgeCallout } from '../types.js'
import { BridgeCalloutList } from './BridgeCalloutList.js'
import { OpenCalloutModal } from './OpenCalloutModal.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'

/** How many closed callouts the card keeps on screen under the open ones. */
const RECENT_SHOWN = 5

interface BridgeCalloutsCardProps {
  facilityId: string
  /** Who is looking: a facility administrator opens and withdraws callouts here; an operator looks. */
  role: SessionRole
}

/**
 * The Clinical Bridge card on the facility dashboard (plan section 7): the
 * open callouts with their pool and who has answered, then the last few
 * closed ones with how they ended. For a facility administrator it has the
 * Open a callout button and a Withdraw on each open callout; an operator
 * sees the same read-only, told who opens them (the user's rule of 13
 * October 2026).
 */
export function BridgeCalloutsCard({ facilityId, role }: BridgeCalloutsCardProps) {
  const callouts = useBridgeCallouts(facilityId)
  const config = useFacilityConfig(facilityId)
  const cancel = useCancelBridgeCallout(facilityId)
  const [isOpening, setIsOpening] = useState(false)
  const [justOpened, setJustOpened] = useState<BridgeCallout | null>(null)
  const canAct = role === 'facility_administrator'
  const isOn = config.data?.clinicalBridge.enabled ?? false

  const open = (callouts.data ?? []).filter((callout) => callout.status === 'open')
  const recent = (callouts.data ?? []).filter((callout) => callout.status !== 'open').slice(0, RECENT_SHOWN)

  return (
    <section aria-label="Clinical Bridge">
      <div className="text-[10px] font-bold uppercase tracking-wide text-marine border-b border-marine/15 pb-2 mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span>
          Clinical Bridge
          <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/bridge/callouts</EndpointHint>
        </span>
        <span className="inline-flex items-center gap-2 normal-case tracking-normal">
          {isOn && <RefreshButton size="sm" onRefresh={() => callouts.refetch()} refreshing={callouts.isFetching} />}
          {canAct && isOn && (
            <Button icon={Siren} size="sm" onClick={() => setIsOpening(true)}>
              Open a callout
            </Button>
          )}
        </span>
      </div>

      {config.isSuccess && !isOn ? (
        <p className="text-xs text-ink-mid">Clinical Bridge is switched off at this facility. Switch it on under Configuration › Clinical Bridge.</p>
      ) : callouts.isPending ? (
        <Skeleton />
      ) : callouts.isError ? (
        <LoadFailure size="xs" message="The callouts could not be loaded." onRetry={() => void callouts.refetch()} retrying={callouts.isFetching} />
      ) : (
        <div className="flex flex-col gap-4">
          {!canAct && <p className="text-[11px] text-ink-mid">A facility administrator opens a callout when a shift needs covering; this is what has been opened here.</p>}
          {justOpened && (
            <p role="status" className="text-xs text-green">
              Callout opened{justOpened.pool.length === 0 ? ', though nobody was enrolled to tell' : `; ${justOpened.pool.length} told`}.
            </p>
          )}
          <div>
            <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Open now ({open.length})</div>
            <BridgeCalloutList
              callouts={open}
              empty="No shift is waiting for cover."
              actions={
                canAct
                  ? (callout) => (
                      <Button icon={CalendarX} variant="secondary" size="sm" disabled={cancel.isPending} onClick={() => cancel.mutate(callout.calloutId)}>
                        Withdraw
                      </Button>
                    )
                  : undefined
              }
            />
            {cancel.isError && <p className="mt-1 text-[11px] text-coral">{cancel.error instanceof Error && cancel.error.message ? cancel.error.message : 'The callout could not be withdrawn.'}</p>}
          </div>
          {recent.length > 0 && (
            <div>
              <div className="mb-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Recent</div>
              <BridgeCalloutList callouts={recent} empty="" />
            </div>
          )}
        </div>
      )}

      {isOpening && (
        <OpenCalloutModal
          facilityId={facilityId}
          onClose={() => setIsOpening(false)}
          onOpened={(callout) => {
            setJustOpened(callout)
          }}
        />
      )}
    </section>
  )
}
